import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";

import MainLayout from "../layouts/MainLayout";
import ErrorLayout from "../layouts/ErrorLayout";
import GuardLayout from "../layouts/GuardLayout";

// Errors
import Error404 from "components/errors/Error404";
import Error500 from "components/errors/Error500";

// Islands in-app
import EmanifestPreview from "components/emanifest/EmanifestPreview";
import ServiceExecutorWorfklow from "components/workflow/ServiceExecutorWorkflow";
import FormViewRenderer from "components/forms/FormViewRenderer";

// Landing/info pages
import Landing from "components/pages/landing/Landing";
import Services from "components/pages/landing/Services";
import FAQ2 from "components/pages/landing/Faq";
import AboutUs from "components/pages/landing/AboutUs";
import Manuals from "components/pages/landing/Manuals";
import Contact from "components/pages/landing/Contact";
import Privacypolicy from "components/pages/landing/Privacypolicy";

// Admin / Related / Forms / Workflows / Misc
import ManageUsers from "components/ManageUsers";
import ManageRoles from "components/ManageRoles";
import LoginActivity from "components/LoginActivity";
import ManageRelatedCompanies from "components/custom/relatedCompanies/ManageRelatedCompanies";
import ManageRelatedGenerators from "components/custom/relatedGenerators/ManageRelatedGenerators";
import ManageContacts from "components/custom/relatedGenerators/ManageContacts";
import ManageTrucks from "components/custom/relatedGenerators/ManageTrucks";
import ManageForms from "components/forms/ManageForms";
import AddForm from "components/forms/AddForm";
import EditForm from "components/forms/EditForm";
import FormBuilderSchema from "components/forms/FormBuilderSchema";
import FormRenderer from "components/forms/FormRenderer";
import FormSubmission from "components/forms/FormSubmission";
import FormViewer from "components/forms/FormViewer";
import Redash from "components/customDashboard/Redash";
import WorkflowList from "components/workflow/WorkflowList";
import WorkflowModeler from "components/workflow/WorkflowModeler";
import WorkflowModelerEditMode from "components/workflow/WorkflowModelerEditMode";
import WorkflowDiagramViewer from "components/workflow/WorkflowDiagramViewer";
import WorkflowHistoryView from "components/workflow/WorkflowHistoryView";
import GetWorkflowListByAssignee from "components/workflow/GetWorkflowListByAssignee";
import GetWorkflowListHistoryByAssignee from "components/workflow/GetWorkflowListHistory";
import GetWorkflowListByCandidateGroup from "components/workflow/GetWorkflowListByCandidateGroup";
import ManageTemplates from "components/workflow/ManageTemplates";
import LoadIFrame from "components/custom/LoadIframe/LoadIFrame";
import FileViewer from "components/fileViewer/FileViewer";
import SupersetDashboard from "components/customDashboard/SupersetDashboard";

import Emanifest from "components/pages/landing/Emanifest";
import AddFacilities from "components/pages/landing/AddFacilities";

import { parseQueryParams } from "components/workflow/WorkflowHelper";
import * as Constants from "common/constants";
import { useTempFormCleanup, useWorkflowStepCleanup } from "../hooks/useRouteCleanup";
import ManageBranches from "components/ManageBranches";
import ImportWizard from "components/ManageImport"

export default function ProtectedRoutes({ loginComponent }) {
	const navigate = useNavigate();
	const location = useLocation();
	const [hasEditFormsPermission] = useState(false);

	useEffect(() => {
		try {
			const qs = parseQueryParams(window.location.href);
			const already = sessionStorage.getItem(Constants.HasRedirected);
			const hasStep = !!localStorage.getItem(Constants.CurrentWorkflowStep);

			if (!already && hasStep && !qs.generatedEmanifestId && location.pathname !== "/userTasks" && !location.pathname.startsWith("/fileViewer")) {
                sessionStorage.setItem(Constants.HasRedirected, "true");
                navigate("/userTasks", { replace: true });
            }

		} catch {}
	}, [location.pathname, navigate]);

	//useWorkflowStepCleanup();
	//useTempFormCleanup();

	return (
		<Routes>
			{loginComponent === "LandingPage" ? (
				<Route path="/" element={<Landing />} />
			) : (
				<Route element={<MainLayout />}>
					<Route path="/" element={<GetWorkflowListByAssignee />} />
				</Route>
			)}

			{/* Errors */}
			<Route element={<ErrorLayout />}>
				<Route path="errors/404" element={<Error404 />} />
				<Route path="errors/500" element={<Error500 />} />
			</Route>

			<Route path="EmanifestPreview/:id" element={<EmanifestPreview />} />

			{/* Main app shell */}
			<Route element={<MainLayout />}>
				<Route path="landing" element={<Landing />} />
				<Route path="aboutus" element={<AboutUs />} />
				<Route path="contact" element={<Contact />} />
				<Route path="emanifest" element={<Emanifest />} />
				<Route path="facilities" element={<AddFacilities />} />
				<Route path="faq" element={<FAQ2 />} />
				<Route path="manuals" element={<Manuals />} />
				<Route path="privacypolicy" element={<Privacypolicy />} />
				<Route path="services" element={<Services />} />

				<Route path="manageBranch" element={<ManageBranches />} />

				{/* in-app islands */}
				<Route path="form_view_renderer" element={<FormViewRenderer />} />
				<Route path="service_executor_workflow" element={<ServiceExecutorWorfklow />} />

				{/* No Need Permission Forms */}
				{hasEditFormsPermission ? <Route path="formedit/:id" element={<EditForm />} /> : null}
				<Route path="formrender/:id" element={<FormRenderer />} />
				<Route path="formView/:id" element={<FormViewer />} />
				<Route path="formsubmission/:path" element={<FormSubmission />} />
				<Route path="fileViewer/*" element={<FileViewer />} />

				{/* No Need Permission Workflows */}
				<Route path="workflow-history-view" element={<WorkflowHistoryView />} />
				<Route path="workflowDiagramViewer" element={<WorkflowDiagramViewer />} />

				<Route element={<GuardLayout />}>
					{window.location.href.includes("inspection") && window.location.href.includes("localhost") ? <Route path="/" element={<GetWorkflowListByAssignee />} /> : <></>}
					{/* Admin */}
					<Route path="manageUsers" element={<ManageUsers />} />
					<Route path="manageRoles" element={<ManageRoles />} />
					<Route path="loginActivity" element={<LoginActivity />} />
					<Route path="manageImport" element={<ImportWizard />} />


					{/* Related */}
					<Route path="manageTransporters" element={<ManageRelatedCompanies RelatedCompanytypeID="3" CompanyRelationshipTypeID="1" />} />
					<Route path="manageFacilities" element={<ManageRelatedCompanies RelatedCompanytypeID="2" CompanyRelationshipTypeID="1" />} />
					<Route path="manageGenerators" element={<ManageRelatedGenerators RelatedCompanytypeNamePlural="Generators" CompanyID="1" />} />
					<Route path="manageContacts" element={<ManageContacts RelatedCompanytypeNamePlural="Contacts" RelatedCompanytypeID="2" CompanyRelationshipTypeID="1" />} />
					<Route path="manageFleet" element={<ManageTrucks RelatedCompanytypeNamePlural="Fleet" CompanyRelationshipTypeID="1" />} />

					{/* Need Permission Forms */}
					<Route path="manageForms" element={<ManageForms />} />
					<Route path="addForm" element={<AddForm />} />
					<Route path="formbuilderschema" element={<FormBuilderSchema />} />

					{/* Need Permission Workflows */}
					<Route path="workflowList" element={<WorkflowList />} />
					<Route path="workflowModelerEditMode" element={<WorkflowModelerEditMode />} />
					<Route path="workflowModeler" element={<WorkflowModeler />} />
					<Route path="userTasks" element={<GetWorkflowListByAssignee />} />
					<Route path="unassignedTasks" element={<GetWorkflowListByCandidateGroup />} />
					<Route path="workflowHistory" element={<GetWorkflowListHistoryByAssignee />} />
					<Route path="manageTemplates" element={<ManageTemplates />} />

					{/* IFrame / Dashboards */}
					<Route path="loadIframe" element={<LoadIFrame />} />
					<Route path="dashboards" element={<SupersetDashboard />} />
				</Route>
			</Route>

			{/* catch-all */}
			<Route path="*" element={<Navigate to="/errors/404" replace />} />
		</Routes>
	);
}
